import { baseApiAuthenticated } from '@/api/v2/index';
import { urlBuilder } from '@/api/utils';

const baseUrl = urlBuilder('order', 'face-validation');

/**
 * #1870. One recorded run of the photo check: the numbers it measured and what it decided with the
 * thresholds in force at the time. No image and no person — the calibration screen replays threshold
 * changes against these values, which needs nothing else.
 */
export type FaceValidationMeasurement = {
    id: string;
    liftOperatorId: string;
    productId: string | null;
    faceCount: number;
    sharpness: number | null;
    brightness: number | null;
    occluded: boolean | null;
    occludedConfidence: number | null;
    sunglasses: boolean | null;
    sunglassesConfidence: number | null;
    ageLow: number | null;
    ageHigh: number | null;
    birthYear: number | null;
    status: string;
    rejectionReason: string | null;
    /** The picture already stored for the keycard — referenced, not copied. */
    imageUrl: string | null;
    /** GOOD = fine and should have passed, BAD = genuinely unusable, null = not looked at yet. */
    humanLabel: 'GOOD' | 'BAD' | null;
    createdAt: string;
};

export const faceValidationApi = baseApiAuthenticated
    .enhanceEndpoints({ addTagTypes: ['FACE_VALIDATION_MEASUREMENTS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getFaceValidationMeasurements: builder.query<
                FaceValidationMeasurement[],
                { liftOperatorId?: string; days?: number }
            >({
                query: ({ liftOperatorId, days }) => {
                    const params = new URLSearchParams();
                    if (liftOperatorId)
                        params.set('liftOperatorId', liftOperatorId);
                    if (days) params.set('days', String(days));
                    const qs = params.toString();
                    return `${baseUrl}/measurements${qs ? `?${qs}` : ''}`;
                },

                providesTags: ['FACE_VALIDATION_MEASUREMENTS'],
            }),

            /**
             * Records what a person judged about the photo. This is what turns the share of
             * rejections into an error rate — the number the go-live gate is written against.
             */
            labelFaceValidationMeasurement: builder.mutation<
                FaceValidationMeasurement,
                { id: string; label: 'GOOD' | 'BAD' }
            >({
                query: ({ id, label }) => ({
                    url: `${baseUrl}/measurements/${id}/label`,
                    method: 'POST',
                    body: { label },
                }),

                invalidatesTags: ['FACE_VALIDATION_MEASUREMENTS'],
            }),
        }),
    });

export const {
    useGetFaceValidationMeasurementsQuery,
    useLabelFaceValidationMeasurementMutation,
} = faceValidationApi;
